import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { IsoCertificationIcon, Typography } from 'components/atoms';
import { NewBreadCrumb } from 'components/molecules';
import { DashboardTile } from 'components/organisms';
import GroupRemoveIcon from '@mui/icons-material/GroupRemove';
import DescriptionIcon from '@mui/icons-material/Description';
import PolicyIcon from '@mui/icons-material/Policy';
import CoPresentOutlinedIcon from '@mui/icons-material/CoPresentOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { AdminPanelSettings } from '@mui/icons-material';
import SupervisorAccountOutlinedIcon from '@mui/icons-material/SupervisorAccountOutlined';
import { ViewScreen } from 'components/template';
import { useAppSelector } from 'store';
import { UserRoles } from 'models/enums';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import { agent } from 'services/service';

export const Dashboard = () => {
  const userRole = useAppSelector(
    (state) => state.auth.user.authData?.data?.userRole
  );
  const [isAssignedUploader, setIsAssignedUploader] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    agent.AttendanceTrackingService.getMyUploadAccess()
      .then((response) => {
        setIsAssignedUploader(response.data.isAssignedUploader);
      })
      .catch(() => setIsAssignedUploader(false));
  }, []);

  const handleRedirection = (id: string) => {
    switch (id) {
      case 'leaves':
        navigate('/LMS');
        break;

      case 'attendance':
        navigate('/my-attendance');
        break;

      case 'admin':
        navigate('/admin/employee-summary-dashboard');
        break;

      case 'manager-portal':
        navigate('/manager');
        break;

      case 'documents':
        window.open(process.env.REACT_APP_FILE_STORAGE, '_blank');
        break;

      case 'hr-policies':
        window.open(process.env.REACT_APP_HR_POLICIES, '_blank');
        break;

      case 'iso-policies':
        window.open(process.env.REACT_APP_ISO_POLICIES, '_blank');
        break;

      case 'iso-presentations':
        window.open(process.env.REACT_APP_ISO_PRESENTATIONS, '_blank');
        break;

      case 'leave-approvals':
        navigate('/leave-approvals');
        break;

      case 'my-goals':
        navigate('/my-goals');
        break;

      case 'supervisor-review':
        navigate('/supervisor-review');
        break;

      case 'attendance-upload':
        navigate('/attendance-tracking/upload');
        break;

      case 'attendance-tracking':
        navigate('/attendance-tracking');
        break;

      case 'my-vehicles':
        navigate('/my-vehicles');
        break;

      default:
        break;
    }
  };

  const headerItem1 = (
    <>
      <Typography type="heading5" text="Aventude Pvt Ltd" />
      <NewBreadCrumb />
    </>
  );
  const mainItem1 = (
    <DashboardTile
      title="Leave"
      type="leaves"
      subtitle="Dashboard"
      icon={<GroupRemoveIcon fontSize="large" />}
      handleNavigation={handleRedirection}
    />
  );
  const mainItem2 = (
    <DashboardTile
      title="Attendance"
      type="attendance"
      subtitle="My Calendar"
      icon={<CalendarMonthOutlinedIcon fontSize="large" />}
      handleNavigation={handleRedirection}
    />
  );
  const mainItem3 = (
    <DashboardTile
      title="HR Guidelines"
      type="documents"
      subtitle="Documents"
      icon={<DescriptionIcon fontSize="large" />}
      handleNavigation={handleRedirection}
    />
  );
  const mainItem4 = (
    <DashboardTile
      title="Admin"
      type="admin"
      subtitle="Portal"
      icon={<AdminPanelSettings fontSize="large" />}
      handleNavigation={handleRedirection}
    />
  );
  const mainItem5 = (
    <DashboardTile
      title="HR Policies"
      type="hr-policies"
      subtitle="Documents"
      icon={<PolicyIcon fontSize="large" />}
      handleNavigation={handleRedirection}
    />
  );
  const mainItem6 = (
    <DashboardTile
      title="My Goals"
      type="my-goals"
      subtitle="Tasks & Goals"
      icon={<TrackChangesIcon fontSize="large" />}
      handleNavigation={handleRedirection}
    />
  );
  const mainItem7 = (
    <DashboardTile
      title="Leave Approval Requests"
      type="leave-approvals"
      subtitle="Leave Requests"
      icon={<AdminPanelSettings fontSize="large" />}
      handleNavigation={handleRedirection}
    />
  );
  const mainItem8 = (
    <DashboardTile
      title="Supervisor Review"
      type="supervisor-review"
      subtitle="Review Tasks"
      icon={<TrackChangesIcon fontSize="large" />}
      handleNavigation={handleRedirection}
    />
  );
  const mainItem9 = (
    <DashboardTile
      title="Attendance Upload"
      type="attendance-upload"
      subtitle="Fingerprint Import"
      icon={<UploadFileOutlinedIcon fontSize="large" />}
      handleNavigation={handleRedirection}
    />
  );
  const mainItem10 = (
    <DashboardTile
      title="Attendance Tracking"
      type="attendance-tracking"
      subtitle="Admin Portal"
      icon={<AdminPanelSettings fontSize="large" />}
      handleNavigation={handleRedirection}
    />
  );
  const mainItem11 = (
    <DashboardTile
      title="Manager Portal"
      type="manager-portal"
      subtitle="Team Attendance & Leaves"
      icon={<SupervisorAccountOutlinedIcon fontSize="large" />}
      handleNavigation={handleRedirection}
    />
  );
  const mainItem12 = (
    <DashboardTile
      title="ISO 27001 Policies"
      type="iso-policies"
      subtitle="Documents"
      icon={<IsoCertificationIcon fontSize="large" />}
      handleNavigation={handleRedirection}
    />
  );
  const mainItem13 = (
    <DashboardTile
      title="ISO Awareness Presentations"
      type="iso-presentations"
      subtitle="Documents"
      icon={<CoPresentOutlinedIcon fontSize="large" />}
      handleNavigation={handleRedirection}
    />
  );
  const mainItem14 = (
    <DashboardTile
      title="My Vehicles"
      type="my-vehicles"
      subtitle="Parking"
      icon={<DirectionsCarOutlinedIcon fontSize="large" />}
      handleNavigation={handleRedirection}
    />
  );

  return (
    <ViewScreen
      mode="dashboard"
      portalTitle="Employee Portal"
      headerItem1={headerItem1}
      mainItem1={mainItem1}
      mainItem2={mainItem2}
      mainItem3={mainItem3}
      mainItem4={userRole === UserRoles.ADMIN ? mainItem4 : null}
      mainItem5={mainItem5}
      mainItem6={mainItem6}
      mainItem7={
        userRole === UserRoles.MANAGER || userRole === UserRoles.ADMIN
          ? mainItem7
          : null
      }
      mainItem8={mainItem8}
      mainItem9={isAssignedUploader ? mainItem9 : null}
      mainItem10={userRole === UserRoles.ADMIN ? mainItem10 : null}
      mainItem11={userRole === UserRoles.MANAGER ? mainItem11 : null}
      mainItem12={mainItem12}
      mainItem13={mainItem13}
      mainItem14={mainItem14}
    />
  );
};
